import { AttributionSearchType } from 'Admin/services/endpoints/PostAttributionSearch';
import {
  PartnerRecordType,
  UserRecordType,
} from 'Admin/services/endpoints/PostAttributionTypeahead';

export type StateType = {
  policies: {
    loading: boolean;
    selections: string[];
    data: AttributionSearchType['policies'];
    pagination: AttributionSearchType['pagination'];
  };
  filters: {
    policyNumber: string;
    states: string[];
  };
  from: {
    agency: PartnerRecordType | null;
    agent?: UserRecordType | null;
  };
  to: {
    agency: PartnerRecordType | null;
    agent: UserRecordType | null;
    effectiveDate?: string;
  };
};

type ActionType =
  | { type: 'policies.loading'; loading: boolean }
  | { type: 'policies.success'; data: AttributionSearchType }
  | { type: 'policies.select'; key: string; selected: boolean }
  | { type: 'policies.selectAll'; selected: boolean }
  | {
      type: 'policies.pagination.update';
      pagination: Partial<AttributionSearchType['pagination']>;
    }
  | { type: 'policies.filters.reset' }
  | { type: 'policies.filters.policyNumber.update'; value: string }
  | { type: 'policies.filters.state.select'; value: string }
  | { type: 'from.agency.set'; value: PartnerRecordType }
  | { type: 'from.agent.set'; value: UserRecordType | null; filtered?: boolean }
  | { type: 'to.agency.set'; value: PartnerRecordType | null }
  | { type: 'to.agent.set'; value: UserRecordType | null }
  | {
      type: 'reattribution.success';
      effectiveDate: string;
      selectedPolicies: Array<string>;
    }
  | {
      type: 'reattribution.reset';
    };

export type ReducerType = [StateType, React.Dispatch<ActionType>];

export const initialState: StateType = {
  policies: {
    loading: false,
    selections: [],
    data: [],
    pagination: {
      limit: 50,
      offset: 0,
      total: 0,
    },
  },
  filters: {
    policyNumber: '',
    states: [],
  },
  from: {
    agency: null,
  },
  to: {
    agency: null,
    agent: null,
  },
};

export const removeSelection = (key: string, arr: string[]) => {
  const a = [...arr];
  const index = a.indexOf(key);
  a.splice(index, 1);
  return a;
};

export const addSelection = (key: string, arr: string[]) => {
  const a = [...arr];
  a.push(key);
  return Array.from(new Set([...a]));
};

export const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case 'policies.loading':
      return {
        ...state,
        policies: {
          ...state.policies,
          loading: action.loading,
        },
      };
    case 'policies.success':
      return {
        ...state,
        policies: {
          ...state.policies,
          loading: false,
          data: action.data.policies,
          pagination: action.data.pagination,
        },
      };
    case 'policies.select':
      return {
        ...state,
        policies: {
          ...state.policies,
          selections: action.selected
            ? addSelection(action.key, state.policies.selections)
            : removeSelection(action.key, state.policies.selections),
        },
      };
    case 'policies.selectAll':
      return {
        ...state,
        policies: {
          ...state.policies,
          selections: action.selected
            ? state.policies.data.map((row) => row.universalPolicyId)
            : [],
        },
      };
    case 'policies.pagination.update':
      return {
        ...state,
        policies: {
          ...state.policies,
          pagination: {
            ...state.policies.pagination,
            ...action.pagination,
          },
        },
      };
    case 'policies.filters.reset':
      return {
        ...state,
        // reset pagination offset on filter
        policies: {
          ...state.policies,
          pagination: {
            ...state.policies.pagination,
            offset: initialState.policies.pagination.offset,
          },
          selections: [],
        },
        filters: {
          ...initialState.filters,
        },
        from: {
          ...state.from,
          agent: null,
        },
      };
    case 'policies.filters.policyNumber.update':
      return {
        ...state,
        // reset pagination offset on filter
        policies: {
          ...state.policies,
          pagination: {
            ...state.policies.pagination,
            offset: initialState.policies.pagination.offset,
          },
          selections: [],
        },
        filters: {
          ...state.filters,
          policyNumber: action.value,
        },
      };
    case 'policies.filters.state.select':
      return {
        ...state,
        // reset pagination offset on filter
        policies: {
          ...state.policies,
          pagination: {
            ...state.policies.pagination,
            offset: initialState.policies.pagination.offset,
          },
          selections: [],
        },
        filters: {
          ...state.filters,
          states: !state.filters.states.includes(action.value)
            ? addSelection(action.value, state.filters.states)
            : removeSelection(action.value, state.filters.states),
        },
      };
    case 'from.agency.set':
      return {
        ...state,
        from: { ...state.from, agency: action.value },
      };
    case 'from.agent.set':
      return {
        ...state,
        from: { ...state.from, agent: action.value },
        ...(action.filtered
          ? {
              // reset pagination offset on filter
              policies: {
                ...state.policies,
                pagination: {
                  ...state.policies.pagination,
                  offset: initialState.policies.pagination.offset,
                },
                selections: [],
              },
            }
          : {}),
      };
    case 'to.agency.set':
      return {
        ...state,
        to: { ...state.to, agency: action.value },
      };
    case 'to.agent.set':
      return {
        ...state,
        to: { ...state.to, agent: action.value },
      };
    case 'reattribution.success':
      return {
        ...state,
        to: { ...state.to, effectiveDate: action.effectiveDate },
        policies: {
          ...state.policies,
          selections: action.selectedPolicies,
        },
      };
    case 'reattribution.reset':
      return {
        ...initialState,
      };
    default:
      return {
        ...state,
      };
  }
};
